import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { Clock, Users, CheckCircle, Hourglass, Bed, Robot } from '@phosphor-icons/react';
import { toast } from 'sonner';
import AIGuideDrawer from '../components/AIGuideDrawer';

const WaitingList = () => {
  const [waitingData, setWaitingData] = useState(null);
  const [nextBatchWaiting, setNextBatchWaiting] = useState(null);
  const [todaysCheckins, setTodaysCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const { activeBranch } = useBranch();
  
  // AI Guide states
  const [showAIGuide, setShowAIGuide] = useState(false);
  const [selectedGuideCustomer, setSelectedGuideCustomer] = useState(null);

  const fetchWaitingList = useCallback(async () => {
    try {
      const response = await api.get('/attendance/waiting-list', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setWaitingData(response.data);
    } catch (error) {
      toast.error('Failed to load waiting list');
    } finally {
      setLoading(false);
    }
  }, [activeBranch]);

  const fetchNextBatchWaiting = useCallback(async () => {
    try {
      const response = await api.get('/attendance/next-batch-waiting', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setNextBatchWaiting(response.data);
    } catch (error) {
      toast.error('Failed to load next batch waiting');
    }
  }, [activeBranch]);

  const fetchTodaysCheckins = useCallback(async () => {
    try {
      const response = await api.get('/attendance/today-checkins', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setTodaysCheckins(response.data.checkins || []);
    } catch (error) {
      toast.error('Failed to load today checkins');
    }
  }, [activeBranch]);

  useEffect(() => {
    if (activeBranch) {
      fetchWaitingList();
      fetchNextBatchWaiting();
      fetchTodaysCheckins();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(() => {
        fetchWaitingList();
        fetchNextBatchWaiting();
        fetchTodaysCheckins();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [activeBranch, fetchWaitingList, fetchNextBatchWaiting, fetchTodaysCheckins]);

  const getCategoryColor = (category) => {
    const colors = {
      'New Customer': 'bg-blue-100 text-blue-800 border-blue-200',
      '10 Day Customer': 'bg-green-100 text-green-800 border-green-200',
      '30 Days Customer': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Buyers': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Challenge Therapy': 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
            <p className="mt-4 text-[#1C1C1A]/60">Loading waiting list...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="waiting-list-page">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-[#2C4C3B] mb-2">
                Batch Waiting List
              </h1>
              <p className="text-sm sm:text-base text-[#1C1C1A]/60">
                Real-time queue showing customers and their allocated batch slots
              </p>
            </div>
            <button
              onClick={fetchWaitingList}
              className="px-4 py-2 bg-[#2C4C3B] text-white rounded-md hover:bg-[#2C4C3B]/90 transition-colors"
              data-testid="refresh-button"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-6 bg-white border-[#E5E5DF]">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Hourglass size={24} className="text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-[#1C1C1A]/60">Waiting for Batch</p>
                <p className="text-3xl font-light text-[#2C4C3B]">
                  {waitingData?.summary?.total_waiting || 0}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white border-[#E5E5DF]">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-[#1C1C1A]/60">In Progress</p>
                <p className="text-3xl font-light text-[#2C4C3B]">
                  {waitingData?.summary?.total_in_progress || 0}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white border-[#E5E5DF]">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CheckCircle size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-[#1C1C1A]/60">Today&apos;s Check-ins</p>
                <p className="text-3xl font-light text-[#2C4C3B]">
                  {todaysCheckins.length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Next Batch Waiting Lists - Regular & Premium */}
        {nextBatchWaiting && (
          <>
            {/* Regular Next Batch */}
            {nextBatchWaiting.next_batches?.regular && nextBatchWaiting.waiting_customers?.regular?.length > 0 && (
              <Card className="p-6 bg-blue-50 border-blue-300 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Clock size={28} className="text-blue-600" weight="fill" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold text-[#2C4C3B]">
                        Waiting for Next Regular Batch
                      </h2>
                      <p className="text-sm text-[#1C1C1A]/60">
                        Next Batch: {nextBatchWaiting.next_batches.regular.start_time} - {nextBatchWaiting.next_batches.regular.end_time} | {nextBatchWaiting.waiting_customers.regular.length} members waiting
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-blue-600">
                      {nextBatchWaiting.waiting_customers.regular.length}
                    </div>
                    <div className="text-sm text-[#1C1C1A]/60">Members</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                  {nextBatchWaiting.waiting_customers.regular.map((customer, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white p-4 rounded-lg border-2 border-blue-300 hover:border-blue-500 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Users size={20} className="text-blue-600" weight="fill" />
                          <span className="font-semibold text-[#2C4C3B]">
                            {customer.customer_name}
                          </span>
                        </div>
                        <Badge className="bg-blue-500 text-white">
                          Token #{customer.token_number}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-[#1C1C1A]/70">
                          <span className="w-16 font-medium">Bed:</span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-900 rounded font-semibold">
                            #{customer.bed_number}
                          </span>
                        </div>
                        {customer.customer_reg_no && (
                          <div className="flex items-center gap-2 text-sm text-[#1C1C1A]/70">
                            <span className="w-16 font-medium">Reg:</span>
                            <span>{customer.customer_reg_no}</span>
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedGuideCustomer({ id: customer.customer_id, name: customer.customer_name });
                          setShowAIGuide(true);
                        }}
                        className="w-full mt-3 bg-teal-500 hover:bg-teal-600 text-white"
                      >
                        <Robot size={16} className="mr-1" weight="fill" />
                        AI Guide
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Premium Next Batch */}
            {nextBatchWaiting.next_batches?.premium && nextBatchWaiting.waiting_customers?.premium?.length > 0 && (
              <Card className="p-6 bg-amber-50 border-amber-300 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-amber-100 rounded-lg">
                      <Clock size={28} className="text-amber-600" weight="fill" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-semibold text-[#2C4C3B]">
                        Waiting for Next Premium Batch
                      </h2>
                      <p className="text-sm text-[#1C1C1A]/60">
                        Next Batch: {nextBatchWaiting.next_batches.premium.start_time} - {nextBatchWaiting.next_batches.premium.end_time} | {nextBatchWaiting.waiting_customers.premium.length} members waiting
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-amber-600">
                      {nextBatchWaiting.waiting_customers.premium.length}
                    </div>
                    <div className="text-sm text-[#1C1C1A]/60">Members</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                  {nextBatchWaiting.waiting_customers.premium.map((customer, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white p-4 rounded-lg border-2 border-amber-300 hover:border-amber-500 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Users size={20} className="text-amber-600" weight="fill" />
                          <span className="font-semibold text-[#2C4C3B]">
                            {customer.customer_name}
                          </span>
                        </div>
                        <Badge className="bg-amber-500 text-white">
                          Token #{customer.token_number}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-[#1C1C1A]/70">
                          <span className="w-16 font-medium">Bed:</span>
                          <span className="px-2 py-1 bg-amber-100 text-amber-900 rounded font-semibold">
                            #{customer.bed_number}
                          </span>
                        </div>
                        {customer.customer_reg_no && (
                          <div className="flex items-center gap-2 text-sm text-[#1C1C1A]/70">
                            <span className="w-16 font-medium">Reg:</span>
                            <span>{customer.customer_reg_no}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {/* Today's Check-ins */}
        <Card className="p-6 bg-white border-[#E5E5DF] mb-6" data-testid="todays-checkins-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" weight="fill" />
              <h2 className="text-2xl font-semibold text-[#2C4C3B]">
                Today&apos;s Check-ins ({todaysCheckins.length})
              </h2>
            </div>
            <p className="text-sm text-[#1C1C1A]/60">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {todaysCheckins.length === 0 ? (
            <p className="text-center py-8 text-[#1C1C1A]/40">No check-ins today</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E5E5DF]">
                    <th className="text-left p-3 text-sm font-semibold text-[#2C4C3B]">Token</th>
                    <th className="text-left p-3 text-sm font-semibold text-[#2C4C3B]">Customer</th>
                    <th className="text-left p-3 text-sm font-semibold text-[#2C4C3B]">Batch</th>
                    <th className="text-left p-3 text-sm font-semibold text-[#2C4C3B]">Bed</th>
                    <th className="text-left p-3 text-sm font-semibold text-[#2C4C3B]">Check-in Time</th>
                    <th className="text-left p-3 text-sm font-semibold text-[#2C4C3B]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {todaysCheckins.map((record, index) => (
                    <tr
                      key={record.attendance_id || index}
                      className="border-b border-[#E5E5DF] hover:bg-[#F9F9F8] transition-colors"
                    >
                      <td className="p-3">
                        <Badge className="bg-blue-600 text-white">
                          #{record.token_number}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="font-semibold text-[#2C4C3B]">{record.customer_name}</p>
                          {record.customer_reg_no && (
                            <p className="text-xs text-[#1C1C1A]/60">{record.customer_reg_no}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="font-medium text-[#2C4C3B]">
                          {record.allocated_batch_time || 'N/A'}
                        </span>
                      </td>
                      <td className="p-3">
                        {record.bed_number ? (
                          <Badge className="bg-green-600 text-white">
                            Bed #{record.bed_number}
                          </Badge>
                        ) : (
                          <span className="text-[#1C1C1A]/40">-</span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-[#1C1C1A]/70">
                        {new Date(record.checked_in_at).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="p-3">
                        {record.status === 'completed' || record.checked_out_at ? (
                          <Badge className="bg-gray-500 text-white">Completed</Badge>
                        ) : record.status === 'waiting' ? (
                          <Badge className="bg-orange-500 text-white">Waiting</Badge>
                        ) : (
                          <Badge className="bg-green-500 text-white">Active</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Currently In Session */}
        <Card className="p-6 bg-white border-[#E5E5DF]" data-testid="in-progress-card">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-6 h-6 text-green-600" />
            <h2 className="text-2xl font-semibold text-[#2C4C3B]">
              Currently In Session ({waitingData?.in_progress?.length || 0})
            </h2>
          </div>

          {waitingData?.in_progress?.length === 0 ? (
            <p className="text-center py-8 text-[#1C1C1A]/40">No active sessions</p>
          ) : (
            <div className="space-y-3">
              {waitingData?.in_progress?.map((record, index) => (
                <div
                  key={record.attendance_id}
                  className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200"
                  data-testid={`in-progress-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-full">
                      <CheckCircle size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-[#2C4C3B]">{record.customer_name}</p>
                      <p className="text-sm text-[#1C1C1A]/60">
                        REG: {record.customer_reg_no} • Batch: {record.allocated_batch_time}
                      </p>
                    </div>
                  </div>
                  
                  {record.time_remaining && (
                    <div className="text-right">
                      <p className="text-sm text-green-700">Time Remaining</p>
                      <p className="text-lg font-semibold text-green-900">{record.time_remaining}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* AI Guide Drawer */}
      <AIGuideDrawer
        isOpen={showAIGuide}
        onClose={() => {
          setShowAIGuide(false);
          setSelectedGuideCustomer(null);
        }}
        customerId={selectedGuideCustomer?.id}
        customerName={selectedGuideCustomer?.name}
      />
    </Layout>
  );
};

export default WaitingList;
